import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import ChangePasswordPage from './ChangePasswordPage';
import EventInterviewAppSimple from './EventInterviewAppSimple';
import { supabase } from '../lib/supabase';

const PressExpiredScreen: React.FC<{ onLogout: () => void }> = ({ onLogout }) => (
  <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
    <div className="bg-white rounded-xl shadow-md max-w-md w-full p-8 text-center" style={{ borderTop: '3px solid var(--fanexpo-magenta)' }}>
      <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--neutral-100)' }}>
        <svg className="w-8 h-8" style={{ color: 'var(--neutral-400)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Press Access Expired</h2>
      <p className="text-gray-600 mb-6">
        Your press credentials are not currently active. If you believe this is an error, please contact the PR team.
      </p>
      <button
        onClick={onLogout}
        className="px-6 py-2 rounded-lg text-white font-medium"
        style={{ background: 'var(--fanexpo-magenta)' }}
      >
        Back to Login
      </button>
    </div>
  </div>
);

const ProtectedApp: React.FC = () => {
  const { user, logout, isLoading } = useAuth();
  const [pressAccessChecked, setPressAccessChecked] = useState(false);
  const [hasPressAccess, setHasPressAccess] = useState(true);

  useEffect(() => {
    const checkPressAccess = async () => {
      if (!user || user.role !== 'journalist') {
        setHasPressAccess(true);
        setPressAccessChecked(true);
        return;
      }

      try {
        // Find active, non-archived years with press_active = true
        const { data: activeYears, error: yearError } = await supabase
          .from('event_settings')
          .select('year')
          .eq('is_archived', false)
          .eq('press_active', true);

        if (yearError) throw yearError;

        if (!activeYears || activeYears.length === 0) {
          setHasPressAccess(false);
          setPressAccessChecked(true);
          return;
        }

        // Check if this journalist has a record in any of those years
        const years = activeYears.map(y => y.year);
        const { data: journalistRecords, error: journalistError } = await supabase
          .from('journalists')
          .select('id')
          .ilike('email', user.email)
          .in('event_year', years)
          .limit(1);

        if (journalistError) throw journalistError;

        setHasPressAccess(journalistRecords && journalistRecords.length > 0);
      } catch (error) {
        console.error('Error checking press access:', error);
        // On error, allow access to avoid locking out users due to a bug
        setHasPressAccess(true);
      }
      setPressAccessChecked(true);
    };

    if (user) {
      checkPressAccess();
    } else {
      setPressAccessChecked(true);
      setHasPressAccess(true);
    }
  }, [user]);

  if (!user) {
    if (isLoading) {
      // Initial session check (loading saved user from localStorage/DB)
      // Don't show LoginPage yet to avoid a flash before session restores
      // BUT: if login() is in progress, LoginPage handles its own "Signing in..." state
      // We distinguish by checking if pressAccessChecked is still false (initial load)
      // vs true (user was on login page, pressAccessChecked was set by effect)
      if (!pressAccessChecked) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          </div>
        );
      }
    }
    return <LoginPage />;
  }

  if (!pressAccessChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user.mustChangePassword) {
    return <ChangePasswordPage />;
  }

  if (!hasPressAccess) {
    return <PressExpiredScreen onLogout={logout} />;
  }

  return <EventInterviewAppSimple />;
};

export default ProtectedApp;
